import React from 'react'
import { theme } from '@clickcease/cc-client-components-lib'
import { format } from 'date-fns'
import {
  ColorString,
  GradientColorObject,
  PatternObject,
  Point,
  SeriesOptionsType,
  XAxisOptions,
  YAxisOptions,
} from 'highcharts'

import { DATE_FORMATS } from 'Utils/dates'

import { AreaSPLineData, BasicSeriesData, DUAL_GRAPH_DATA_INDEX } from './types'

import { renderToString } from 'react-dom/server'
import { TrendsTooltip } from './components/TrendsTooltip/TrendsTooltip'
import i18n from 'Lib/i18n'

const formatter = Intl.NumberFormat('en', { notation: 'compact' }) //format 10000 => 10k

const defaultLineColor = (
  index: (typeof DUAL_GRAPH_DATA_INDEX)[keyof typeof DUAL_GRAPH_DATA_INDEX]
) => {
  return index === DUAL_GRAPH_DATA_INDEX.FIRST
    ? theme.colors.chart10.value
    : theme.colors.chart11.value
}

const seriesColorOptions: (
  index: (typeof DUAL_GRAPH_DATA_INDEX)[keyof typeof DUAL_GRAPH_DATA_INDEX]
) => ColorString | GradientColorObject | PatternObject = (index) => ({
  linearGradient: {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 1,
  },
  stops: [
    [
      0,
      index === DUAL_GRAPH_DATA_INDEX.FIRST
        ? theme.colors.chart12.value
        : theme.colors.chart13.value,
    ],
    [1, theme.colors.white.value],
  ],
})

const commonYAxisOptions: YAxisOptions = {
  title: {
    text: '',
  },
}

const yAxis: Array<YAxisOptions> = [
  {
    // left y axis
    ...commonYAxisOptions,
    labels: {
      formatter: ({ value }) => formatter.format(value as number),
    },
    gridLineWidth: 1,
    gridLineDashStyle: 'Dash',
  },
  {
    // right y axis
    ...commonYAxisOptions,
    labels: {
      formatter: ({ value }) => {
        return `${value}%`
      },
    },
    opposite: true,
    gridLineWidth: 1,
    gridLineDashStyle: 'Dash',
  },
]

const xAxis: XAxisOptions = {
  type: 'datetime',
  labels: {
    formatter: ({ value }) => {
      return format(value as number, DATE_FORMATS.MONTH_DAY_FULL_YEAR)
    },
  },
  title: {
    text: '',
  },
  gridLineWidth: 1,
  gridLineDashStyle: 'Dash',
}

const defaultChartSeriesOptions: SeriesOptionsType = {
  type: 'area',
  marker: {
    enabled: false,
    symbol: 'circle',
  },
  dataLabels: {
    enabled: false,
  },
  enableMouseTracking: true,
  lineWidth: 2,
}

const renderToolTip = (value: Point, title: string, percentInTitle: boolean) => {
  return renderToString(
    <TrendsTooltip value={value} title={title} percentInTitle={percentInTitle} />
  )
}

const seriesTooltip: (title: string, percentInTitle?: boolean) => Highcharts.TooltipOptions = (
  title,
  percentInTitle = false
) => ({
  useHTML: true,
  headerFormat: '',
  pointFormatter(this: Point) {
    return renderToolTip(this, i18n.t(title, { ns: 'dashboard' }), percentInTitle)
  },
})

export const chartOptions: (areaSPLineData: AreaSPLineData) => Highcharts.Options = (
  areaSPLineData
) => ({
  tooltip: {
    enabled: true,
    useHTML: true,
  },
  legend: {
    enabled: true,
    symbolRadius: 2,
  },
  series: areaSPLineData
    .filter((option: BasicSeriesData) => option?.data && option?.name)
    .map(({ name, data, percentInTitle, color, fillColor, sameYAxis }, index) => ({
      ...defaultChartSeriesOptions,
      name,
      yAxis: sameYAxis ? 0 : index,
      data,
      color:
        color ||
        defaultLineColor(
          index as (typeof DUAL_GRAPH_DATA_INDEX)[keyof typeof DUAL_GRAPH_DATA_INDEX]
        ),
      fillColor:
        fillColor ||
        seriesColorOptions(
          index as (typeof DUAL_GRAPH_DATA_INDEX)[keyof typeof DUAL_GRAPH_DATA_INDEX]
        ),
      tooltip: seriesTooltip(name, percentInTitle),
    })),
  yAxis,
  xAxis: {
    ...xAxis,
    type: 'datetime',
    labels: {
      format: '{value:%b %d}',
    },
  },
})
